import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ResidencyEnum } from "../types/doctor.definition";
import { AcceptedCountriesEnum } from "../types/user.definition";

// Single location object schema (for patients)
export const locationSchema = (t: TFunction) =>
  z.object({
    id: z.number().optional(),
    address: z.string().optional().or(z.literal("")),
    state: z.string().min(1, t("common.validation.required")),
    country: z
      .enum([AcceptedCountriesEnum.SENEGAL, AcceptedCountriesEnum.CHAD], {
        message: t("common.validation.required"),
      })
      .optional()
      .or(z.literal("")),
    residency: z
      .enum([
        ResidencyEnum.HOME,
        ResidencyEnum.SECOND_HOME,
        ResidencyEnum.OTHER,
      ])
      .optional(),
    isPrimary: z.boolean().optional(),
    coordinates: z
      .object({
        lat: z.number(),
        lng: z.number(),
      })
      .nullable()
      .optional(),
    city: z.string().optional().or(z.literal("")),
    zip: z.string().optional().or(z.literal("")),
  });

// Base location object schema for arrays
const locationObjectSchema = (t: TFunction) =>
  z
    .object({
      id: z.number().optional(),
      address: z.string(),
      state: z.string(),
      country: z.enum(
        [AcceptedCountriesEnum.SENEGAL, AcceptedCountriesEnum.CHAD],
        {
          message: t("common.validation.required"),
        }
      ),
      residency: z
        .enum([
          ResidencyEnum.HOME,
          ResidencyEnum.SECOND_HOME,
          ResidencyEnum.OTHER,
        ])
        .optional()
        .or(z.undefined()),
      isPrimary: z.boolean().optional(),
      coordinates: z
        .object({
          lat: z.number(),
          lng: z.number(),
        })
        .nullable()
        .optional(),
      city: z.string().optional().or(z.literal("")),
      zip: z.string().optional().or(z.literal("")),
    })
    .refine((loc) => loc.address.trim().length > 0, {
      message: t("common.validation.required"),
      path: ["address"],
    })
    .refine((loc) => loc.state.trim().length > 0, {
      message: t("common.validation.required"),
      path: ["state"],
    });

// Array of locations schema (for profiles - requires at least one)
export const locationsArraySchema = (t: TFunction) =>
  z
    .array(locationObjectSchema(t))
    .min(1, t("profile.validation.atLeastOneLocation"));

// Optional array of locations schema (for staff forms - can be empty)
export const optionalLocationsArraySchema = (t: TFunction) =>
  z.array(locationObjectSchema(t)).optional();

export const useLocationSchema = () => {
  const { t } = useTranslation();
  return locationSchema(t);
};

export type LocationSchema = z.infer<ReturnType<typeof useLocationSchema>>;
